import { useMemo } from 'react';
import { parseToHsl } from 'polished';
import { useGlobalSettingsThemeParameters } from './theme';
import { ReactDesignTokens, renderReactDesignTokens } from '../design-tokens/render/react';
import { ThemeParameters } from '../design-tokens';
import { useLanguage } from '../locale/useTextLocalizer';

/**
 * Derive values for the `dt` object
 *
 * @param inverseTheme - specify whether or not to derive values for the inverse theme
 */
export function useDeriveDesignTokens(
  contextValue: { currentTheme: ThemeParameters; inverseTheme: ThemeParameters },
  inverted?: boolean
): ReactDesignTokens {
  const globalSettingsOverrides = useGlobalSettingsThemeParameters();
  const language = useLanguage();

  const dt = useMemo(() => {
    const customFocusColor = globalSettingsOverrides.customFocusColor;
    const customFocusOpacity = globalSettingsOverrides.customFocusOpacity;
    const customFocusThickness = globalSettingsOverrides.customFocusThickness;
    const theme = inverted ? contextValue.inverseTheme : contextValue.currentTheme;
    const dt = renderReactDesignTokens(theme, { lang: language });

    function generateCustomFocusColor(tokenColor: string) {
      if (customFocusColor || customFocusOpacity) {
        const hsl = parseToHsl(customFocusColor ?? tokenColor);
        // since statefocusnova token does not support opacity, using hsla format for color
        return `hsla(${Math.round(hsl.hue)}, ${Math.round(hsl.saturation * 100)}%, ${Math.round(
          hsl.lightness * 100
        )}%, ${customFocusOpacity ? parseFloat(customFocusOpacity) / 100 : '1'})`;
      }
      return tokenColor;
    }

    return theme.enableNovaTokens
      ? {
          ...dt,
          stateFocusNova: {
            ...dt.stateFocusNova,
            onLightBg: {
              ...dt.stateFocusNova.onLightBg,
              bd: generateCustomFocusColor(dt.stateFocusNova.onLightBg.bd),
              bdWidth: customFocusThickness ?? dt.stateFocusNova.onLightBg.bdWidth,
            },
            onDarkBg: {
              ...dt.stateFocusNova.onDarkBg,
              bd: generateCustomFocusColor(dt.stateFocusNova.onDarkBg.bd),
              bdWidth: customFocusThickness ?? dt.stateFocusNova.onDarkBg.bdWidth,
            },
            onRightClick: {
              ...dt.stateFocusNova.onRightClick,
              bd: generateCustomFocusColor(dt.stateFocusNova.onRightClick.bd),
              bdWidth: customFocusThickness ?? dt.stateFocusNova.onRightClick.bdWidth,
            },
          },
        }
      : {
          ...dt,
          stateFocus: {
            ...dt.stateFocus,
            onLightBg: {
              ...dt.stateFocus.onLightBg,
              bd: generateCustomFocusColor(dt.stateFocus.onLightBg.bd),
              bdWidth: customFocusThickness ?? dt.stateFocus.onLightBg.bdWidth,
            },
            onDarkBg: {
              ...dt.stateFocus.onDarkBg,
              bd: generateCustomFocusColor(dt.stateFocus.onDarkBg.bd),
              bdWidth: customFocusThickness ?? dt.stateFocus.onDarkBg.bdWidth,
            },
            onRightClick: {
              ...dt.stateFocus.onRightClick,
              bd: generateCustomFocusColor(dt.stateFocus.onRightClick.bd),
              bdWidth: customFocusThickness ?? dt.stateFocus.onRightClick.bdWidth,
            },
          },
        };
  }, [
    globalSettingsOverrides.customFocusColor,
    globalSettingsOverrides.customFocusOpacity,
    globalSettingsOverrides.customFocusThickness,
    inverted,
    contextValue.inverseTheme,
    contextValue.currentTheme,
    language,
  ]);

  return dt;
}
